import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { ALL_CHAMPIONS_LEAGUE_TEAMS, CHAMPIONS_LEAGUE_TEAMS } from '../../../graphql/queries';
import { CREATE_CHAMPIONS_LEAGUE_TEAMS, UPDATE_CHAMPIONS_LEAGUE_TEAMS } from '../../../graphql/mutations';
import Notification from '../../../components/Notification/Notification';
import useNotification from '../../../hooks/useNotification';
import './ChampionsLeagueTeamsAdmin.css';

const ChampionsLeagueTeamsAdmin = () => {
  const { showNotification, notificationProps } = useNotification();
  const [teams, setTeams] = useState(['']);
  const [season, setSeason] = useState(`${new Date().getFullYear()}/${new Date().getFullYear() + 1}`);
  const [isEditing, setIsEditing] = useState(false);

  const { data: allTeamsData, loading: allTeamsLoading, refetch: refetchAllTeams } = useQuery(ALL_CHAMPIONS_LEAGUE_TEAMS);
  const { data: activeTeamsData, loading: activeTeamsLoading, refetch: refetchActiveTeams } = useQuery(CHAMPIONS_LEAGUE_TEAMS);
  
  const [createTeams] = useMutation(CREATE_CHAMPIONS_LEAGUE_TEAMS);
  const [updateTeams] = useMutation(UPDATE_CHAMPIONS_LEAGUE_TEAMS);

  const activeTeams = activeTeamsData?.championsLeagueTeams;
  const allTeams = allTeamsData?.allChampionsLeagueTeams || [];

  // Sikr at der altid er præcis 36 hold
  useEffect(() => {
    if (teams.length !== 36) {
      const newTeams = [...teams];
      while (newTeams.length < 36) {
        newTeams.push('');
      }
      setTeams(newTeams);
    }
  }, [teams.length]);

  const handleAddTeam = () => {
    setTeams([...teams, '']);
  };

  // Sikr at der altid er mindst 36 hold
  const ensureMinimumTeams = () => {
    if (teams.length < 36) {
      const newTeams = [...teams];
      while (newTeams.length < 36) {
        newTeams.push('');
      }
      setTeams(newTeams);
    }
  };

  const handleRemoveTeam = (index) => {
    if (teams.length > 1) {
      const newTeams = teams.filter((_, i) => i !== index);
      setTeams(newTeams);
    }
  };

  const handleTeamChange = (index, value) => {
    const newTeams = [...teams];
    newTeams[index] = value;
    setTeams(newTeams);
  };

  const handleLoadActiveTeams = () => {
    if (activeTeams) {
      // Konverter Team objekter til string array for input felterne
      const teamsArray = activeTeams.teams.map(team => team.name);
      
      // Sikr at der er præcis 36 hold
      while (teamsArray.length < 36) {
        teamsArray.push('');
      }
      setTeams(teamsArray);
      setSeason(activeTeams.season);
      setIsEditing(true);
    }
  };

  const handleCreateNewSeason = () => {
    setTeams(['']);
    // Hent nuværende år fra sæsonen og opdater til næste sæson
    const currentYear = parseInt(season.split('/')[0]);
    setSeason(`${currentYear + 1}/${currentYear + 2}`);
    setIsEditing(false);
  };

  const handleSave = async () => {
    let validTeams = [];
    
    try {
      // Sikr at der er præcis 36 hold før validering
      ensureMinimumTeams();
      
      validTeams = teams.filter(team => team && team.trim() !== '').map(team => team.trim());
      
      if (validTeams.length !== 36) {
        showNotification(`Du skal have præcis 36 hold. Du har ${validTeams.length} hold.`, 'error');
        return;
      }

      // Tjek for duplikater
      const uniqueTeams = new Set(validTeams);
      if (uniqueTeams.size !== validTeams.length) {
        showNotification('Du har duplikerede hold. Hver hold må kun optræde én gang.', 'error');
        return;
      }

      // Konverter string array til TeamInput objekter
      const teamInputs = validTeams.map((teamName, index) => ({
        name: teamName,
        points: 0,
        position: index + 1
      }));

      if (isEditing) {
        await updateTeams({
          variables: { teams: teamInputs }
        });
        showNotification('Hold opdateret!', 'success');
      } else {
        await createTeams({
          variables: { teams: teamInputs, season }
        });
        showNotification(`Hold for sæson ${season} oprettet!`, 'success');
      }

      await refetchAllTeams();
      await refetchActiveTeams();
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving teams:', error);
      console.error('Teams being saved:', validTeams);
      console.error('Season:', season);
      console.error('Is editing:', isEditing);
      
      let errorMessage = 'Fejl ved gemning af hold: ';
      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        errorMessage += error.graphQLErrors[0].message;
      } else if (error.networkError) {
        errorMessage += 'Netværksfejl: ' + error.networkError.message;
      } else {
        errorMessage += error.message;
      }
      
      showNotification(errorMessage, 'error');
    }
  };

  if (allTeamsLoading || activeTeamsLoading) {
    return <div className="loading">Henter hold data...</div>;
  }

  return (
    <div className="champions-league-teams-admin">
      <h2>Champions League Hold Administration</h2>
      <Notification {...notificationProps} />
      <div className="admin-section">
        <h3>Aktive Hold</h3>
        {activeTeams ? (
          <div className="active-teams-info">
            <p><strong>Sæson:</strong> {activeTeams.season}</p>
            <p><strong>Antal hold:</strong> {activeTeams.teams.length}</p>
            <p><strong>Opdateret:</strong> {new Date(activeTeams.updatedAt).toLocaleDateString('da-DK')}</p>
            <button 
              className="edit-button"
              onClick={handleLoadActiveTeams}
            >
              Rediger Aktive Hold
            </button>
          </div>
        ) : (
          <p>Ingen aktive hold fundet. Opret hold for nuværende sæson.</p>
        )}
      </div>

      <div className="admin-section">
        <h3>{isEditing ? 'Rediger Hold' : 'Opret Nye Hold'}</h3>
        
        <div className="season-input">
          <label htmlFor="season">Sæson:</label>
          <input
            id="season"
            type="text"
            value={season}
            onChange={(e) => setSeason(e.target.value)}
            disabled={isEditing}
            placeholder="2025/2026"
          />
        </div>

        <div className="teams-input">
          <div className="teams-header">
            <h4>Hold ({teams.filter(t => t.trim() !== '').length}/36)</h4>
            <button 
              className="add-team-button"
              onClick={handleAddTeam}
            >
              + Tilføj Hold
            </button>
          </div>

          <div className="teams-list">
            {teams.map((team, index) => (
              <div key={index} className="team-input-row">
                <input
                  type="text"
                  value={team}
                  onChange={(e) => handleTeamChange(index, e.target.value)}
                  placeholder={`Hold ${index + 1}`}
                  className="team-input"
                />
                <button
                  className="remove-team-button"
                  onClick={() => handleRemoveTeam(index)}
                  disabled={teams.length === 1}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="admin-actions">
          <button 
            className="create-new-button"
            onClick={handleCreateNewSeason}
          >
            Opret Ny Sæson
          </button>
          <button 
            className="save-button"
            onClick={handleSave}
            disabled={teams.filter(t => t.trim() !== '').length !== 36}
          >
            {isEditing ? 'Opdater Hold' : 'Opret Hold'}
          </button>
        </div>
      </div>

      <div className="admin-section">
        <h3>Alle Sæsoner</h3>
        <div className="seasons-list">
          {allTeams.map((teamData) => (
            <div 
              key={teamData.id} 
              className={`season-item ${teamData.isActive ? 'active' : ''}`}
            >
              <div className="season-info">
                <h4>Sæson {teamData.season}</h4>
                <p>Hold: {teamData.teams.length}</p>
                <p>Status: {teamData.isActive ? 'Aktiv' : 'Inaktiv'}</p>
                <p>Oprettet: {new Date(teamData.createdAt).toLocaleDateString('da-DK')}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ChampionsLeagueTeamsAdmin;
